import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Money,
} from '@mlm/ui';
import { Decimal } from '@mlm/shared';
import { api } from '../lib/api';
import { downloadBlob } from '../lib/download';

function hasFee(value: string): boolean {
  return new Decimal(value).gt(0);
}

type DeductionStep = {
  key: string;
  label: string;
  mode: 'percent' | 'flat';
  value: string;
  base: string;
  amount: string;
};

/**
 * The deductions this request was charged, in the order they were applied. The
 * tenant can reorder deductions globally, so the dialog reads the request's own
 * snapshot rather than a hardcoded sequence. Requests issued before the snapshot
 * carried `steps` fall back to the legacy order off the named columns.
 */
function deductionSteps(r: PayoutRequest): DeductionStep[] {
  if (Array.isArray(r.deductionSteps) && r.deductionSteps.length > 0) return r.deductionSteps;
  const legacy: DeductionStep[] = [];
  const push = (key: string, label: string, amount: string) => {
    if (hasFee(amount)) legacy.push({ key, label, mode: 'flat', value: '0', base: '0', amount });
  };
  push('service_charge', 'Service charge', r.serviceCharge);
  push('admin_fee', 'Admin fee', r.adminFee);
  push('withholding_tax', 'Withholding tax', r.withholdingTax);
  return legacy;
}

/** Fetch a batch's CSV (auth header attached by the api client) and download it. */
async function exportPayoutBatch(id: string) {
  const res = await api.get(`/admin/payouts/batches/${id}/export`, { responseType: 'blob' });
  downloadBlob(res.data as BlobPart, `payout-batch-${id}.csv`, 'text/csv');
}

function batchStatusVariant(
  status: PayoutBatch['status'],
): 'success' | 'destructive' | 'warning' | 'secondary' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'rejected':
      return 'destructive';
    case 'processing':
      return 'warning';
    default:
      return 'secondary';
  }
}

type PayoutRequest = {
  id: string;
  userId: string;
  amount: string;
  currency: string;
  method: string;
  /** Readable method name resolved from the tenant's payout-method registry. */
  methodLabel: string;
  /** The member's cashout account details, one labeled row per method field. */
  account: { label: string; value: string }[];
  /**
   * The member's payout QR as snapshotted when they requested — a short-lived
   * presigned URL, or null if they had none saved for this method. The raw
   * object key never reaches the client.
   */
  qrUrl?: string | null;
  /** The member's own label for that QR, when they set one. */
  qrLabel?: string | null;
  serviceCharge: string;
  adminFee: string;
  withholdingTax: string;
  netAmount: string;
  /** The applied deduction sequence, snapshotted when the request was issued. */
  deductionSteps?: DeductionStep[];
  status: 'requested' | 'queued' | 'processing' | 'paid' | 'rejected' | 'cancelled';
  batchId: string | null;
  requestedAt: string;
  processedAt: string | null;
  rejectionReason: string | null;
  userName: string | null;
  userEmail: string;
  userUsername: string | null;
  slotCode: string;
};

/** Best readable name for a requester, falling back through email → username → id. */
function requesterName(r: PayoutRequest): string {
  return r.userName?.trim() || r.userEmail || r.userUsername || `${r.userId.slice(0, 8)}…`;
}

type PayoutBatch = {
  id: string;
  periodStart: string;
  periodEnd: string;
  total: string;
  currency: string;
  status: 'queued' | 'processing' | 'paid' | 'rejected';
  createdAt: string;
  processedAt: string | null;
};

/** One member's line on the disbursement worklist. */
type BatchItem = {
  requestId: string;
  amount: string;
  netAmount: string;
  method: string;
  methodLabel: string;
  memberName: string | null;
  memberEmail: string;
  status: string;
  account: { label: string; value: string }[];
  /** Short-lived presigned URL for the member's payout QR (null if none saved). */
  qrUrl: string | null;
  qrLabel: string | null;
};

type BatchDetail = {
  batch: PayoutBatch;
  items: BatchItem[];
  totals: { gross: string; net: string };
};

export function PayoutsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<PayoutRequest | null>(null);
  const [batchDetail, setBatchDetail] = useState<string | null>(null);

  const reqQ = useQuery({
    queryKey: ['payouts-requested'],
    queryFn: async () =>
      (
        await api.get<PayoutRequest[]>('/admin/payouts/requests', {
          params: { status: 'requested', page: 1, pageSize: 100 },
        })
      ).data,
  });

  const batchesQ = useQuery({
    queryKey: ['payouts-batches'],
    queryFn: async () =>
      (await api.get<PayoutBatch[]>('/admin/payouts/batches', { params: { page: 1, pageSize: 50 } }))
        .data,
  });

  const createBatch = useMutation({
    mutationFn: async () => {
      // Use the current month as the batch period range — a reasonable default
      // for kick-the-tires; admin UI v2 will let you pick.
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
      await api.post('/admin/payouts/batches', {
        periodStart,
        periodEnd,
        requestIds: [...selected],
      });
    },
    meta: { successMessage: 'Payout batch created.' },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['payouts-requested'] });
      qc.invalidateQueries({ queryKey: ['payouts-batches'] });
    },
  });

  const markPaid = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/admin/payouts/batches/${id}/mark-paid`);
    },
    meta: { successMessage: 'Batch marked paid.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts-batches'] });
      qc.invalidateQueries({ queryKey: ['payouts-requested'] });
    },
  });

  const rejectBatch = useMutation({
    mutationFn: async (args: { id: string; reason: string }) => {
      await api.post(`/admin/payouts/batches/${args.id}/reject`, {
        rejectionReason: args.reason,
      });
    },
    meta: { successMessage: 'Batch rejected.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts-batches'] });
      qc.invalidateQueries({ queryKey: ['payouts-requested'] });
    },
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Requested Payouts</CardTitle>
          <Button
            disabled={selected.size === 0 || createBatch.isPending}
            onClick={() => createBatch.mutate()}
          >
            {createBatch.isPending
              ? 'Batching…'
              : `Batch ${selected.size} request${selected.size === 1 ? '' : 's'}`}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {reqQ.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !reqQ.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">No requested payouts.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium w-8"></th>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">User</th>
                  <th className="px-4 py-2 font-medium">Method</th>
                  <th className="px-4 py-2 font-medium text-right">Gross</th>
                  <th className="px-4 py-2 font-medium text-right">Service</th>
                  <th className="px-4 py-2 font-medium text-right">Admin</th>
                  <th className="px-4 py-2 font-medium text-right">WTAX</th>
                  <th className="px-4 py-2 font-medium text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {reqQ.data.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                    onClick={() => setDetail(r)}
                  >
                    <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggle(r.id)}
                        aria-label={`Select request ${r.id.slice(0, 8)}`}
                      />
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(r.requestedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">{requesterName(r)}</td>
                    <td className="px-4 py-2">{r.methodLabel}</td>
                    <td className="px-4 py-2 text-right">
                      <Money amount={r.amount} direction="neutral" />
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">
                      {hasFee(r.serviceCharge) ? (
                        <Money amount={r.serviceCharge} direction="debit" />
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">
                      {hasFee(r.adminFee) ? (
                        <Money amount={r.adminFee} direction="debit" />
                      ) : (
                        ''
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">
                      {hasFee(r.withholdingTax) ? (
                        <Money amount={r.withholdingTax} direction="debit" />
                      ) : (
                        ''
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">
                      <Money amount={r.netAmount} currency={r.currency} direction="neutral" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Batches</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {batchesQ.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !batchesQ.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">No batches yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Created</th>
                  <th className="px-4 py-2 font-medium text-right">Total</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {batchesQ.data.map((b) => (
                  <BatchRow
                    key={b.id}
                    batch={b}
                    onMarkPaid={() => markPaid.mutateAsync(b.id)}
                    onReject={(reason) => rejectBatch.mutate({ id: b.id, reason })}
                    onView={() => setBatchDetail(b.id)}
                    busy={markPaid.isPending || rejectBatch.isPending}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <RequestDetailDialog request={detail} onClose={() => setDetail(null)} />
      <BatchDetailDialog batchId={batchDetail} onClose={() => setBatchDetail(null)} />
    </div>
  );
}

/**
 * The disbursement worklist for one batch: every member in it, what they're
 * owed, where to send it, and their payout QR to scan.
 *
 * This exists because the batch is where money actually moves. The requested-
 * payouts table only lists `status: 'requested'`, so once a batch was created
 * the account details and QR were unreachable in the UI — the CSV is text-only
 * and deliberately carries no QR.
 */
function BatchDetailDialog({ batchId, onClose }: { batchId: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['payout-batch', batchId],
    queryFn: async () => (await api.get<BatchDetail>(`/admin/payouts/batches/${batchId}`)).data,
    enabled: !!batchId,
  });

  return (
    <Dialog open={!!batchId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Batch payout details</DialogTitle>
        </DialogHeader>

        {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {q.isError && <p className="text-sm text-destructive">Could not load this batch.</p>}

        {q.data && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
              <span className="text-muted-foreground">
                {q.data.items.length} payout{q.data.items.length === 1 ? '' : 's'}
              </span>
              <span>
                Net{' '}
                <Money
                  amount={q.data.totals.net}
                  currency={q.data.batch.currency}
                  direction="neutral"
                />
              </span>
            </div>

            {q.data.items.map((it) => (
              <div key={it.requestId} className="rounded-md border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{it.memberName || it.memberEmail}</div>
                    <div className="text-xs text-muted-foreground">{it.memberEmail}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{it.methodLabel}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <Money amount={it.netAmount} currency={q.data!.batch.currency} direction="neutral" />
                    <div className="text-xs text-muted-foreground">net</div>
                  </div>
                </div>

                <div className="mt-2 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    {it.account.length ? (
                      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs">
                        {it.account.map((f, i) => (
                          <div key={i} className="contents">
                            <dt className="text-muted-foreground">{f.label}</dt>
                            <dd className="break-all">{f.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <p className="text-xs text-muted-foreground">No account details on file.</p>
                    )}
                  </div>
                  {it.qrUrl && (
                    <a
                      href={it.qrUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={it.qrLabel ? `Payout QR — ${it.qrLabel}` : 'Payout QR — open full size'}
                      className="shrink-0"
                    >
                      <img
                        src={it.qrUrl}
                        alt={`Payout QR for ${it.memberName || it.memberEmail}`}
                        className="h-24 w-24 rounded border bg-background object-contain"
                      />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Read-only detail popup for a single requested payout. */
function RequestDetailDialog({
  request,
  onClose,
}: {
  request: PayoutRequest | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Payout request</DialogTitle>
        </DialogHeader>

        {request && (
          <div className="space-y-3 text-sm">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 border rounded-md p-3 bg-muted/30">
              <dt className="text-muted-foreground">Member</dt>
              <dd>
                <div>{requesterName(request)}</div>
                {request.userEmail && (
                  <div className="text-xs text-muted-foreground">{request.userEmail}</div>
                )}
                {request.userUsername && (
                  <div className="text-xs text-muted-foreground">@{request.userUsername}</div>
                )}
              </dd>
              <dt className="text-muted-foreground">Slot</dt>
              <dd className="font-mono text-xs">{request.slotCode}</dd>
              <dt className="text-muted-foreground">Requested</dt>
              <dd>{new Date(request.requestedAt).toLocaleString()}</dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge variant="secondary">{request.status}</Badge>
              </dd>
              <dt className="text-muted-foreground">Method</dt>
              <dd>{request.methodLabel}</dd>
              {request.processedAt && (
                <>
                  <dt className="text-muted-foreground">Processed</dt>
                  <dd>{new Date(request.processedAt).toLocaleString()}</dd>
                </>
              )}
              {request.rejectionReason && (
                <>
                  <dt className="text-muted-foreground">Rejection reason</dt>
                  <dd>{request.rejectionReason}</dd>
                </>
              )}
            </dl>

            <div className="border rounded-md p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Cashout details</p>
              {request.account.length ? (
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
                  {request.account.map((f, i) => (
                    <div key={i} className="contents">
                      <dt className="text-muted-foreground">{f.label}</dt>
                      <dd className="break-all">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="text-xs text-muted-foreground">No account details on file.</p>
              )}
              {request.qrUrl && (
                <div className="mt-3 border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Payout QR{request.qrLabel ? ` — ${request.qrLabel}` : ''}
                  </p>
                  {/* Snapshotted at request time, so this is the QR as it was
                      when the member asked — not whatever they have saved now.
                      Click to open full size for scanning. */}
                  <a href={request.qrUrl} target="_blank" rel="noreferrer" title="Open full size">
                    <img
                      src={request.qrUrl}
                      alt="Payout QR code"
                      className="h-32 w-32 rounded border bg-background object-contain"
                    />
                  </a>
                </div>
              )}
            </div>

            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 border rounded-md p-3">
              <dt className="text-muted-foreground">Gross</dt>
              <dd>
                <Money amount={request.amount} currency={request.currency} direction="neutral" />
              </dd>
              {/* Render the snapshot's applied sequence — the tenant can reorder
                  deductions globally, and this dialog must show what THIS
                  request was actually charged, in that request's own order. */}
              {deductionSteps(request).map((s, i) => (
                <Fragment key={`${s.key}-${i}`}>
                  <dt className="text-muted-foreground">
                    {s.label}
                    {s.mode === 'percent' && Number(s.value) > 0
                      ? ` (${String(Number(s.value))}% of ${Number(s.base).toLocaleString()})`
                      : ''}
                  </dt>
                  <dd>
                    <Money amount={s.amount} direction="debit" />
                  </dd>
                </Fragment>
              ))}
              <dt className="text-muted-foreground font-medium">Net</dt>
              <dd className="font-medium">
                <Money amount={request.netAmount} currency={request.currency} direction="neutral" />
              </dd>
            </dl>
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchRow({
  batch,
  onMarkPaid,
  onReject,
  onView,
  busy,
}: {
  batch: PayoutBatch;
  onMarkPaid: () => Promise<void>;
  onReject: (reason: string) => void;
  onView: () => void;
  busy: boolean;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const isFinal = batch.status === 'paid' || batch.status === 'rejected';
  const shortId = batch.id.slice(0, 8);

  return (
    <>
      <tr className="border-b">
        <td className="px-4 py-2 text-xs text-muted-foreground">
          {new Date(batch.createdAt).toLocaleString()}
        </td>
        <td className="px-4 py-2 text-right">
          <Money amount={batch.total} currency={batch.currency} direction="neutral" />
        </td>
        <td className="px-4 py-2">
          <Badge variant={batchStatusVariant(batch.status)}>{batch.status}</Badge>
        </td>
        <td className="px-4 py-2 text-right space-x-2">
          <Button size="sm" variant="ghost" onClick={onView}>
            View
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title="Download the bank/e-wallet disbursement sheet for this batch"
            onClick={() => exportPayoutBatch(batch.id)}
          >
            Export for bank
          </Button>
          {!isFinal && (
            <>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  setConfirmError(null);
                  setConfirming(true);
                }}
              >
                Mark paid
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => setRejecting(!rejecting)}
              >
                Reject
              </Button>
            </>
          )}
        </td>
      </tr>
      {rejecting && (
        <tr className="bg-muted/30">
          <td colSpan={4} className="px-4 py-3">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Rejection reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="destructive"
                disabled={!reason.trim() || busy}
                onClick={() => {
                  onReject(reason.trim());
                  setRejecting(false);
                  setReason('');
                }}
              >
                Confirm reject
              </Button>
            </div>
          </td>
        </tr>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={(next) => {
          if (!next) {
            setConfirming(false);
            setConfirmError(null);
          }
        }}
        title="Mark batch paid"
        description={
          <span>
            Flips every request in this batch to <code>paid</code>. Closes the
            payout-hold ledger entries. The action is recorded in the audit log.
          </span>
        }
        confirmLabel="Mark paid"
        loading={busy}
        errorMessage={confirmError}
        typedConfirm={{ expected: `MARK PAID ${shortId}` }}
        onConfirm={async () => {
          setConfirmError(null);
          try {
            await onMarkPaid();
            setConfirming(false);
          } catch (err) {
            setConfirmError(err instanceof Error ? err.message : 'Failed to mark paid.');
          }
        }}
      >
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm border rounded-md p-3 bg-muted/30">
          <dt className="text-muted-foreground">Batch</dt>
          <dd className="font-mono text-xs">{shortId}…</dd>
          <dt className="text-muted-foreground">Created</dt>
          <dd>{new Date(batch.createdAt).toLocaleString()}</dd>
          <dt className="text-muted-foreground">Total</dt>
          <dd><Money amount={batch.total} currency={batch.currency} direction="neutral" /></dd>
        </dl>
      </ConfirmDialog>
    </>
  );
}
